import { useEffect, useRef } from 'react';

/** Atributos de acessibilidade de um controle com erro: `aria-invalid` e ligação com a mensagem `<id>-erro` do `Rotulo`. */
export function ariaErro(id: string, erro?: string | null) {
  return { 'aria-invalid': erro ? (true as const) : undefined, 'aria-describedby': erro ? `${id}-erro` : undefined };
}

function visivel(el: HTMLElement) {
  return el.offsetParent !== null || el === document.activeElement;
}

/** Foco no primeiro controle marcado `aria-invalid` (na ordem da tela) ou, sem campo, na mensagem geral (`role="alert"`). */
export function focarErro(raiz: ParentNode): boolean {
  const campo = [...raiz.querySelectorAll<HTMLElement>('[aria-invalid="true"]')].find((c) => visivel(c) && !(c as HTMLInputElement).disabled);
  if (campo) {
    campo.focus();
    return true;
  }
  const alerta = [...raiz.querySelectorAll<HTMLElement>('[role="alert"]')].find(visivel);
  if (alerta) {
    if (!alerta.hasAttribute('tabindex')) alerta.tabIndex = -1;
    alerta.focus();
    return true;
  }
  return false;
}

/**
 * Validação local dos formulários próprios dos módulos (os que não usam o `Formulario` do núcleo): sempre que
 * `erros` (por campo) ou `geral` (mensagem do topo) mudam para algo não vazio, o foco vai para o primeiro campo
 * inválido ou, se não houver, para a mensagem. Sem `ref` ligado ao `<form>`, procura na camada aberta (modal ou
 * drawer) ou na página. Devolve o `ref` para ligar ao `<form>` e `ariaErro` para os controles.
 */
export function useValidacaoLocal(erros?: Record<string, string> | null, geral?: string | null) {
  garantirGuardaDatas();
  const ref = useRef<HTMLFormElement>(null);
  const temErro = !!geral || Object.keys(erros ?? {}).length > 0;
  useEffect(() => {
    if (!temErro) return;
    const camadas = document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]');
    const raiz = ref.current ?? camadas[camadas.length - 1] ?? document.querySelector('main') ?? document.body;
    focarErro(raiz);
  }, [erros, geral]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ref, ariaErro };
}

/**
 * Guarda de datas inválidas (r5 robustez M4). O `CampoData` mostra "Data inválida" mas o valor do formulário fica no
 * último valor bom (ou vazio): um `<form noValidate>` próprio enviaria `null` (ex.: chave de API sem expiração).
 * Esta guarda barra o envio de qualquer formulário ou camada (modal, drawer) que tenha um campo de data com texto
 * inválido ou incompleto: foco no campo e mensagem (`reportValidity` dispara `invalid`, que o `CampoData` mostra).
 * Vale para `submit` de `<form>` (captura no `document`) e para o clique de `Botao` primário (`Botao` chama
 * `bloquearDataInvalida`). O `Formulario` do núcleo tem a proteção própria e se marca com `data-datas-proprias`.
 */
export function primeiraDataInvalida(raiz: ParentNode): HTMLInputElement | null {
  const campos = [...raiz.querySelectorAll<HTMLInputElement>('input[data-data-invalida]')];
  return campos.find((c) => !c.disabled) ?? null;
}

/** Devolve true (e leva o foco ao campo, com a mensagem) quando há data inválida em `raiz`; false para seguir o envio. */
export function bloquearDataInvalida(raiz: ParentNode): boolean {
  if ((raiz as Element).hasAttribute?.('data-datas-proprias')) return false;
  const campo = primeiraDataInvalida(raiz);
  if (!campo) return false;
  campo.focus();
  campo.reportValidity?.();
  return true;
}

function aoEnviarForm(e: Event) {
  const form = e.target as HTMLFormElement | null;
  if (!form || form.tagName !== 'FORM') return;
  if (bloquearDataInvalida(form)) {
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}

/** Instala (uma vez) a captura de `submit` no `document`; também roda ao carregar este módulo, antes de qualquer formulário. */
export function garantirGuardaDatas() {
  if (typeof document === 'undefined') return;
  const w = window as unknown as { __guardaDatas?: boolean };
  if (w.__guardaDatas) return;
  w.__guardaDatas = true;
  document.addEventListener('submit', aoEnviarForm, true);
}

garantirGuardaDatas();
